import React, { useState, useEffect } from 'react';
import { KeyRound, X, RefreshCw, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../context/ToastContext';
import { usePasswordPolicy, evaluatePassword } from '../hooks/usePasswordPolicy';
import PasswordRequirements from './common/PasswordRequirements';

interface AdminPasswordResetModalProps {
  open: boolean;
  onClose: () => void;
  workspaceId: number;
  workspaceName: string;
  adminEmail: string;
}

interface JobStatus {
  job_id: number;
  status: string;
  progress: number;
  step_label: string;
  error_message?: string;
  result_message?: string;
  target_email: string;
}

const AdminPasswordResetModal: React.FC<AdminPasswordResetModalProps> = ({
  open,
  onClose,
  workspaceId,
  workspaceName,
  adminEmail,
}) => {
  const { showSuccess, showError } = useToast();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<number | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);
  const [submitHovered, setSubmitHovered] = useState(false);
  const [closeHovered, setCloseHovered] = useState(false);
const { policy: passwordPolicy } = usePasswordPolicy();

  useEffect(() => {
    if (open) {
      setNewPassword('');
      setConfirmPassword('');
      setShowPassword(false);
      setValidationError(null);
      setJobId(null);
      setJobStatus(null);
      setIsPolling(false);
    }
  }, [open]);

  useEffect(() => {
    if (!isPolling || !jobId) return;

    const pollStatus = async () => {
      try {
        const response = await apiService.get(`/workspaces/${workspaceId}/admin-password-reset/${jobId}`);
        const status: JobStatus = response.data;

        setJobStatus(status);

        if (status.status === 'completed') {
          setIsPolling(false);
          showSuccess('Admin password reset successfully');
        } else if (status.status === 'failed') {
          setIsPolling(false);
        }
      } catch (err: any) {
        console.error('Error polling job status:', err);
        showError(err.response?.data?.error || 'Failed to check status');
        setIsPolling(false);
      }
    };

    pollStatus();
    const interval = setInterval(pollStatus, 3000);
    return () => clearInterval(interval);
  }, [isPolling, jobId, workspaceId]);

  const validatePassword = (): boolean => {
    if (!newPassword) {
      setValidationError('Please enter a password');
      return false;
    }
if (Array.from(newPassword).length < passwordPolicy.minLength) {
      setValidationError(`Password must be at least ${passwordPolicy.minLength} characters`);
      return false;
    }
    if (newPassword !== confirmPassword) {
      setValidationError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setValidationError(null);
    if (!validatePassword()) return;

    try {
      setLoading(true);
      const response = await apiService.post(`/workspaces/${workspaceId}/admin-password-reset`, {
        new_password: newPassword,
      });
      setJobId(response.data.job_id);
      setIsPolling(true);
      setLoading(false);
    } catch (err: any) {
      console.error('Error creating password reset job:', err);
      showError(err.response?.data?.error || 'Failed to initiate password reset');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (isPolling) return;
    onClose();
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = 'var(--v2-primary)';
    e.target.style.boxShadow = '0 0 0 3px var(--v2-primary-muted)';
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = 'var(--v2-border)';
    e.target.style.boxShadow = 'none';
  };

  if (!open) return null;

  const canSubmit = !loading && !!newPassword && newPassword === confirmPassword &&
    evaluatePassword(newPassword, passwordPolicy).meetsClientChecks;

  const renderContent = () => {
    // Progress state
    if (isPolling || (jobStatus && jobStatus.status !== 'completed' && jobStatus.status !== 'failed')) {
      return (
        <div style={{ padding: '20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--v2-primary-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <RefreshCw
                size={16}
                strokeWidth={2}
                color="var(--v2-primary)"
                style={{ animation: 'spin 1s linear infinite' }}
              />
            </div>
            <span style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--v2-text)' }}>
              {jobStatus?.step_label || 'Processing...'}
            </span>
          </div>

          <div
            style={{
              height: '8px',
              backgroundColor: 'var(--v2-border)',
              borderRadius: 'var(--radius-full)',
              overflow: 'hidden',
              marginBottom: '12px',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${jobStatus?.progress || 0}%`,
                backgroundColor: 'var(--v2-primary)',
                borderRadius: 'var(--radius-full)',
                transition: 'width 0.3s ease',
              }}
            />
          </div>

          <p
            style={{
              fontSize: '0.875rem',
              color: 'var(--v2-text-tertiary)',
              textAlign: 'center',
              margin: '0 0 20px 0',
            }}
          >
            {jobStatus?.progress || 0}% complete
          </p>

          <div
            style={{
              backgroundColor: 'var(--v2-surface-alt)',
              borderLeft: '4px solid var(--v2-primary)',
              borderRadius: '0 var(--radius-md) var(--radius-md) 0',
              padding: '14px 16px',
            }}
          >
            <p style={{ fontSize: '0.875rem', color: 'var(--v2-text)', margin: 0 }}>
              Please wait while we reset the admin password. This typically takes 30-60 seconds.
            </p>
          </div>
        </div>
      );
    }

    // Success state
    if (jobStatus?.status === 'completed') {
      return (
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--v2-success-bg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}
          >
            <CheckCircle size={32} strokeWidth={1.5} color="var(--v2-success)" />
          </div>

          <div
            style={{
              backgroundColor: 'var(--v2-success-bg)',
              border: '1px solid #86efac',
              borderRadius: 'var(--radius-md)',
              padding: '16px',
              marginBottom: '16px',
            }}
          >
            <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--v2-success-dark)', margin: '0 0 6px 0' }}>
              Password Reset Successful
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--v2-success)', margin: 0 }}>
              {jobStatus.result_message || `Password has been reset for ${adminEmail}`}
            </p>
          </div>

          <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>
            You can now log into your workspace using the new password.
          </p>
        </div>
      );
    }

    // Error state
    if (jobStatus?.status === 'failed') {
      return (
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--v2-error-bg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}
          >
            <XCircle size={32} strokeWidth={1.5} color="var(--v2-error)" />
          </div>

          <div
            style={{
              backgroundColor: 'var(--v2-error-bg)',
              border: '1px solid #fca5a5',
              borderRadius: 'var(--radius-md)',
              padding: '16px',
              marginBottom: '16px',
            }}
          >
            <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--v2-error)', margin: '0 0 6px 0' }}>
              Password Reset Failed
            </p>
            <p style={{ fontSize: '0.875rem', color: '#b91c1c', margin: 0 }}>
              {jobStatus.error_message || 'An error occurred during password reset'}
            </p>
          </div>

          <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>
            Please try again or contact support if the problem persists.
          </p>
        </div>
      );
    }

    // Form state
    return (
      <div>
        {/* Target Info */}
        <div
          style={{
            backgroundColor: 'var(--v2-surface-alt)',
            borderLeft: '4px solid var(--v2-primary)',
            borderRadius: '0 var(--radius-md) var(--radius-md) 0',
            padding: '14px 16px',
            marginBottom: '24px',
          }}
        >
          <p
            style={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: 'var(--v2-text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              margin: '0 0 4px 0',
            }}
          >
            Resetting password for
          </p>
          <p style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--v2-text)', margin: 0 }}>{adminEmail}</p>
        </div>

        {validationError && (
          <div
            style={{
              backgroundColor: 'var(--v2-error-bg)',
              border: '1px solid #fca5a5',
              borderRadius: 'var(--radius-md)',
              padding: '12px 16px',
              marginBottom: '20px',
            }}
          >
            <p style={{ fontSize: '0.875rem', color: 'var(--v2-error)', margin: 0 }}>{validationError}</p>
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: 'var(--v2-text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '8px',
            }}
          >
            New Password
          </label>
          <input
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setValidationError(null);
            }}
            placeholder="Enter new password"
            style={{
              width: '100%',
              padding: '10px 12px',
              backgroundColor: 'var(--v2-surface)',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              color: 'var(--v2-text)',
              outline: 'none',
              transition: 'border-color 0.15s, box-shadow 0.15s',
              boxSizing: 'border-box',
            }}
onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            autoFocus
          />
          <PasswordRequirements
            password={newPassword}
            policy={passwordPolicy}
            columns={1}
          />
          <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', margin: '6px 0 0 0' }}>
	Minimum {passwordPolicy.minLength} characters
          </p>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: 'var(--v2-text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '8px',
            }}
          >
            Confirm Password
          </label>
          <input
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setValidationError(null);
            }}
            placeholder="Confirm new password"
            style={{
              width: '100%',
              padding: '10px 12px',
              backgroundColor: 'var(--v2-surface)',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              color: 'var(--v2-text)',
              outline: 'none',
              transition: 'border-color 0.15s, box-shadow 0.15s',
              boxSizing: 'border-box',
            }}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
          />
        {confirmPassword && newPassword !== confirmPassword && (
          <p style={{ fontSize: '0.8125rem', color: 'var(--v2-error)', margin: '6px 0 0 0' }}>
            Passwords do not match
          </p>
        )}
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', margin: '-12px 0 20px 0', cursor: 'pointer' }}>
          <input type="checkbox" checked={showPassword} onChange={(e) => setShowPassword(e.target.checked)} />
          Show passwords
        </label>
        {/* Warning */}
        <div
          style={{
            backgroundColor: 'var(--v2-warning-bg)',
            border: '1px solid #fcd34d',
            borderRadius: 'var(--radius-md)',
            padding: '14px 16px',
          }}
        >
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#92400e', margin: '0 0 4px 0' }}>
            Need to change the admin login?
          </p>
          <p style={{ fontSize: '0.8125rem', color: '#a16207', margin: 0 }}>
            Contact support to update the admin login for security verification.
          </p>
        </div>
      </div>
    );
  };

  const renderFooter = () => {
    if (jobStatus?.status === 'completed' || jobStatus?.status === 'failed') {
      return (
        <button
          onClick={onClose}
          onMouseEnter={() => setCloseHovered(true)}
          onMouseLeave={() => setCloseHovered(false)}
          style={{
            padding: '10px 20px',
            backgroundColor: closeHovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
            color: '#ffffff',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'background-color 0.15s',
          }}
        >
          Close
        </button>
      );
    }

    if (isPolling) return null;

    return (
      <>
        <button
          onClick={onClose}
          disabled={loading}
          onMouseEnter={() => setCancelHovered(true)}
          onMouseLeave={() => setCancelHovered(false)}
          style={{
            padding: '10px 20px',
            backgroundColor: cancelHovered && !loading ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
            color: cancelHovered && !loading ? 'var(--v2-text)' : 'var(--v2-text-tertiary)',
            border: '1px solid var(--v2-border)',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1,
            transition: 'all 0.15s',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          onMouseEnter={() => setSubmitHovered(true)}
          onMouseLeave={() => setSubmitHovered(false)}
          style={{
            padding: '10px 20px',
            backgroundColor: !canSubmit ? 'var(--v2-border)' : submitHovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
            color: !canSubmit ? 'var(--v2-text-muted)' : '#ffffff',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: !canSubmit ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.15s',
            minWidth: '140px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          {loading && <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />}
          {loading ? 'Submitting...' : 'Reset Password'}
        </button>
      </>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPolling) handleClose();
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--v2-border)',
          maxWidth: '480px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'var(--font-sans)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--v2-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--v2-primary-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <KeyRound size={20} strokeWidth={1.5} color="var(--v2-primary)" />
            </div>
            <div>
              <h2
                style={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: 'var(--v2-text)',
                  margin: 0,
                  letterSpacing: '-0.025em',
                }}
              >
                Reset Admin Password
              </h2>
              <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: '2px 0 0 0' }}>
                {workspaceName}
              </p>
            </div>
          </div>
          {!isPolling && (
            <button
              onClick={handleClose}
              style={{
                background: 'none',
                border: 'none',
                padding: '8px',
                cursor: 'pointer',
                borderRadius: 'var(--radius-sm)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--v2-text-muted)',
                transition: 'all 0.15s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--v2-surface-hover)';
                e.currentTarget.style.color = 'var(--v2-text)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--v2-text-muted)';
              }}
            >
              <X size={20} strokeWidth={1.5} />
            </button>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>{renderContent()}</div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--v2-border)',
            backgroundColor: 'var(--v2-surface-alt)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
          }}
        >
          {renderFooter()}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AdminPasswordResetModal;
